import { Component } from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import {
  ChargeInformationDto,
  ChargeInformationService,
  ChargePriceDeltaDto,
  ChargePriceDto,
  ChargeSetupDto,
  ObservationTimeSeriesPeriod
} from '@app/api/dmc';
import { chargeinformationPriceMasterdata } from '@shared/interfaces/dmc/masterdata/chargeinformation-price';
import { ActivatedRoute, ParamMap } from '@angular/router';
import { LoadingService } from '@app/core/services/loading.service';
import { isoToRealDate } from '@app/core/utils/iso-to-real-date';
import { CardComponent } from '@shared/components/card/card.component';
import { TranslatePipe } from '@ngx-translate/core';
import { ValueDetailsComponent } from '@shared/components/value-details/value-details.component';
import { InputGroupComponent } from '@shared/components/input/input-group/input-group.component';
import { LabelDirective } from '@shared/components/input/label/label.directive';
import { InputDirective } from '@shared/components/input/input/input.directive';
import { Columns } from '@shared/interfaces/table/column-config';
import { ChevronLeft, ChevronRight, LucideAngularModule } from 'lucide-angular';

@Component({
  selector: 'app-chargeinformation-prices-overview',
  imports: [
    CardComponent,
    TranslatePipe,
    ValueDetailsComponent,
    ReactiveFormsModule,
    InputGroupComponent,
    LabelDirective,
    InputDirective,
    LucideAngularModule,
    FormsModule
  ],
  templateUrl: './chargeinformation-prices-overview.component.html',
  styleUrl: './chargeinformation-prices-overview.component.css'
})
export class ChargeinformationPricesOverviewComponent {
  /** Icons **/
  protected readonly ChevronRight = ChevronRight;
  protected readonly ChevronLeft = ChevronLeft;

  public id?: string;

  /** Date handler **/
  public startDate: string = new Date().toISOString().split('T')[0];
  public minStartDate?: string;
  public endDate: string = new Date().toISOString().split('T')[0];

  public form: FormGroup = new FormGroup({
    chargeTypeId: new FormControl(null, Validators.required),
    observationPeriod: new FormControl(null),
    validFrom: new FormControl(new Date().toISOString().split('T')[0]),
    validTo: new FormControl(new Date().toISOString().split('T')[0]),
  });
  public chargePriceDelta?: ChargePriceDeltaDto;
  public chargePrices?: ChargePriceDto[] = [];
  public chargeInformation?: ChargeInformationDto;

  public priceCoreData: Columns<ChargePriceDeltaDto>[] = chargeinformationPriceMasterdata;
  public singlePriceData: Columns<ChargePriceDto>[] = [
    {
      title: 'chargeprices.price',
      key: 'energyPrice',
    },
  ]

  public intervalForm: FormGroup = new FormGroup({});
  public interval: number[] = Array(24).fill(0).map((_, i) => i);

  constructor(
    private readonly chargeInformationService: ChargeInformationService,
    private readonly route: ActivatedRoute,
    private readonly loadingService: LoadingService,
  ) {
    this.interval.forEach(hour => {
      this.intervalForm.addControl(`hour_${hour}`, new FormControl(0));
    });

    this.route.parent?.paramMap.subscribe((q: ParamMap) => {
      this.id = q.get('id')!;

      this.fetchAndUpdateChargeInformation();
    })
  }

  public fetchAndUpdateChargeInformation() {
    this.chargeInformationService.chargeInformationGetByIdGet(this.id).subscribe((chargeInformation: ChargeInformationDto) => {
      this.chargeInformation = chargeInformation;

      this.chargeInformationService.chargeInformationGetChargeSetupGet(chargeInformation.id!).subscribe(chargeSetupCollection => {
        const firstChargeSetupValidFrom = this.getFirstChargeSetupValidFrom(chargeSetupCollection.results ?? []);
        const chargeInformationValidFrom = chargeInformation.validFrom
          ? isoToRealDate(chargeInformation.validFrom)
          : undefined;

        this.startDate = firstChargeSetupValidFrom ?? chargeInformationValidFrom ?? this.startDate;
        this.minStartDate = firstChargeSetupValidFrom ?? chargeInformationValidFrom;
        this.form.patchValue({
          chargeTypeId: chargeInformation.chargeTypeId,
          observationPeriod: chargeInformation.observationPeriod,
          validFrom: this.startDate,
        });
        this.search();
      });
    });
  }

  private getFirstChargeSetupValidFrom(chargeSetups: ChargeSetupDto[]): string | undefined {
    const validFromDates = chargeSetups
      .filter(chargeSetup => !chargeSetup.hiddenPrice && chargeSetup.validFrom)
      .map(chargeSetup => isoToRealDate(chargeSetup.validFrom!))
      .filter((date): date is string => !!date)
      .sort((a, b) => a.localeCompare(b));

    return validFromDates[0];
  }

  public search(): void {
    if (this.form.valid) {
      this.loadingService.start();

      this.chargeInformationService.chargeInformationQueryChargePricePost({
        ...this.form.value,
      }).subscribe((result: ChargePriceDeltaDto) => {
          this.chargePriceDelta = { ...result };
          this.chargePrices = result?.chargePrices;
          if (this.chargeInformation?.observationPeriod === 'Hour' && this.chargePrices?.length === 24) {
            this.setHours();
          }
          this.loadingService.stop();
        }
      );
    }
  }

  public setHours(): void {
    if (this.chargePrices) {
      const newValues: { [key: string]: number } = {};
      this.chargePrices.forEach((price, index) => {
        if (price.energyPrice) {
          newValues[`hour_${index}`] = parseFloat(price.energyPrice.toFixed(4));
        }
      });
      this.intervalForm.patchValue(newValues);
    }
  }

  public previous(): void {
    if (this.isPreviousDisabled()) {
      return;
    }

    const currentDate = new Date(this.form.get('validFrom')?.value);
    currentDate.setDate(currentDate.getDate() - 1);
    this.startDate = currentDate.toISOString().split('T')[0];

    this.form.patchValue({
      validFrom: this.startDate
    });
    this.search();
  }

  public next(): void {
    const currentDate = new Date(this.form.get('validFrom')?.value);
    currentDate.setDate(currentDate.getDate() + 1);
    this.startDate = currentDate.toISOString().split('T')[0];
    this.form.patchValue({
      validFrom: this.startDate
    });
    this.search();
  }

  public onStartDateChange(date: string): void {
    const d = new Date(date);

    this.form.get('validFrom')?.setValue(d.toISOString().split('T')[0]);

    this.search();
  }

  public isPreviousDisabled(): boolean {
    return !!this.minStartDate && this.startDate <= this.minStartDate;
  }

  protected readonly ObservationTimeSeriesPeriod = ObservationTimeSeriesPeriod;
}
